import React, { useRef, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Progress,
  Input,
  Icon,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Divider
} from '@chakra-ui/react';
import { FiUpload, FiFile, FiCheck, FiX, FiEye } from 'react-icons/fi';

const DocumentUploadCard = ({
  documentType,
  onUpload,
  progress,
  uploadedDocs = [],
  isUploading = false,
  error = null
}) => {
  const fileInputRef = useRef();
  const toast = useToast();

  const getDocumentTypeLabel = (type) => {
    const labels = {
      business_license: 'Business License',
      tax_certificate: 'Tax Certificate',
      identity_proof: 'Identity Proof',
      bank_statement: 'Bank Statement',
      incorporation_certificate: 'Incorporation Certificate',
      other: 'Other Document'
    };
    return labels[type] || type;
  };

  const getDocumentDescription = (type) => {
    const descriptions = {
      business_license: 'Valid business license or registration certificate',
      tax_certificate: 'Tax registration certificate or EIN documentation',
      identity_proof: 'Government-issued ID (passport, driver\'s license, etc.)',
      bank_statement: 'Recent bank statement showing business account',
      incorporation_certificate: 'Certificate of incorporation or business formation',
      other: 'Additional supporting documents'
    };
    return descriptions[type] || 'Supporting document for verification';
  };

  const isRequired = (type) => {
    return ['business_license', 'tax_certificate', 'identity_proof'].includes(type);
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    // Validate files
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

    for (const file of files) {
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: `${file.name} is larger than 10MB. Please choose a smaller file.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid file type',
          description: `${file.name} is not a supported file type. Please upload PDF, JPG, or PNG files.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    // Call parent upload handler
    onUpload(documentType.key, files);

    // Reset input
    event.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'green';
      case 'rejected': return 'red';
      case 'under_review': return 'yellow';
      case 'pending': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified': return 'Verified';
      case 'rejected': return 'Rejected';
      case 'under_review': return 'Under Review';
      case 'pending': return 'Pending';
      default: return 'Unknown';
    }
  };

  return (
    <Box
      w="full"
      p={{ base: 4, md: 6 }}
      border="1px"
      borderColor="secondary.700"
      borderRadius="lg"
      bg="secondary.900"
      transition="all 0.2s"
      _hover={{ borderColor: "secondary.600" }}
      position="relative"
      opacity={isUploading ? 0.7 : 1}
    >
      {/* Header */}
      <HStack justify="space-between" mb={4}>
        <VStack align="start" spacing={1}>
          <HStack>
            <Text fontWeight="bold" color="primary.100" fontSize="lg">
              {getDocumentTypeLabel(documentType.key)}
              {isUploading && " (Uploading...)"}
            </Text>
            {isRequired(documentType.key) && (
              <Badge colorScheme="red" size="sm">Required</Badge>
            )}
            {isUploading && (
              <Badge colorScheme="blue" size="sm">Uploading</Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="primary.900">
            {getDocumentDescription(documentType.key)}
          </Text>
          <Text fontSize="xs" color="primary.900">
            Accepted: PDF, JPG, PNG (Max 10MB each)
          </Text>
        </VStack>

        <Button
          size={{ base: "sm", md: "md" }}
          leftIcon={!isUploading ? <Icon as={FiUpload} /> : undefined}
          onClick={() => fileInputRef.current?.click()}
          isDisabled={isUploading}
          isLoading={isUploading}
          loadingText="Uploading..."
          variant="outline"
          bg="secondary.900"
          borderColor="secondary.700"
          color="primary.100"
          _hover={{ bg: "dark.500", borderColor: "primary.100" }}
          _disabled={{
            opacity: 0.6,
            cursor: "not-allowed",
            _hover: { bg: "secondary.900", borderColor: "secondary.700" }
          }}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </HStack>

      {/* Hidden file input */}
      <Input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileSelection}
        display="none"
      />

      {/* Upload Progress */}
      {progress !== undefined && progress < 100 && (
        <Box mb={4}>
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="primary.100">Uploading...</Text>
            <Text fontSize="sm" color="primary.100">{progress}%</Text>
          </HStack>
          <Progress
            value={progress}
            size="sm"
            colorScheme="blue"
            bg="dark.500"
            borderRadius="md"
          />
        </Box>
      )}

      {/* Error Display */}
      {error && (
        <Alert status="error" mb={4} bg="red.900" borderColor="red.700">
          <AlertIcon color="red.300" />
          <Box>
            <AlertTitle color="red.100">Upload Failed!</AlertTitle>
            <AlertDescription color="red.200">{error}</AlertDescription>
          </Box>
        </Alert>
      )}

      {/* Uploaded Documents */}
      {uploadedDocs.length > 0 && (
        <Box>
          <Divider borderColor="secondary.700" mb={4} />
          <Text fontSize="sm" fontWeight="medium" color="primary.100" mb={3}>
            Uploaded Documents ({uploadedDocs.length})
          </Text>
          <VStack spacing={3} align="stretch">
            {uploadedDocs.map((doc, index) => (
              <Flex
                key={index}
                align="center"
                justify="space-between"
                p={3}
                bg="secondary.900"
                borderRadius="md"
                border="1px"
                borderColor="secondary.700"
              >
                <HStack spacing={3} flex={1}>
                  <Icon as={FiFile} color="primary.100" boxSize={4} />
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium" color="primary.100">
                      {doc.original_filename || doc.filename}
                    </Text>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="primary.900">
                        {formatFileSize(doc.file_size || 0)}
                      </Text>
                      <Text fontSize="xs" color="primary.900">•</Text>
                      <Text fontSize="xs" color="primary.900">
                        {new Date(doc.upload_date).toLocaleDateString()}
                      </Text>
                    </HStack>
                  </VStack>
                  <Badge
                    colorScheme={getStatusColor(doc.verification_status)}
                    size="sm"
                    variant="solid"
                  >
                    {getStatusText(doc.verification_status)}
                  </Badge>
                </HStack>

                {doc.verification_status === 'verified' && (
                  <Icon as={FiCheck} color="green.400" boxSize={5} />
                )}
                {doc.verification_status === 'rejected' && (
                  <Icon as={FiX} color="red.400" boxSize={5} />
                )}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      {/* Empty State */}
      {uploadedDocs.length === 0 && !isUploading && !error && (
        <Box
          textAlign="center"
          py={6}
          border="2px dashed"
          borderColor="secondary.700"
          borderRadius="md"
          bg="dark.500"
        >
          <Icon as={FiUpload} boxSize={8} color="primary.900" mb={2} />
          <Text fontSize="sm" color="primary.900" mb={1}>
            No documents uploaded yet
          </Text>
          <Text fontSize="xs" color="primary.900">
            Click "Upload" to add your {getDocumentTypeLabel(documentType.key).toLowerCase()}
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default DocumentUploadCard;